// UX-Vorschlag P3: EIN Detailbereich fuer persoenliche und Projektaufgaben.
// Reine Logik: aus einem TaskRecord (gemeinsames Modell, lib/taskRecord.ts)
// das Anzeige-Modell des Detailbereichs bauen — gleiche Kopfzeile, gleiche
// vier Kernfelder, typspezifisches eingeklappt darunter — plus die
// Tastatur-Navigation (↑/↓) durch die sichtbare Liste.
//
// Datenmodelle bleiben getrennt (Adapter statt Migration): Projektaufgaben
// haben eine Checkliste und Sub-Aufgaben, persoenliche Aufgaben „Schritte".
// Beides erscheint hier als „Schritte"; das Befoerdern macht aus einem
// Checklistenpunkt eine Sub-Aufgabe bzw. aus einem Schritt eine eigene Aufgabe.
//
// Beschreibung: Projektaufgaben speichern MARKDOWN (`description`), persoenliche
// Aufgaben HTML (`notes`). Ein gemeinsamer Editor wuerde das gespeicherte
// Format der jeweils anderen Seite aendern und den bisherigen Editor (classic,
// iOS, MCP) mit fremdem Markup fuettern. Deshalb bleibt das Format pro Typ,
// bis eine Server-Migration es vereinheitlicht (offener Punkt).

import type { PersonalTask, Task } from "@juuplan/shared";
import type { TaskRecord } from "./taskRecord";

export type DescriptionFormat = "markdown" | "html";

export interface DetailField {
  label: string;
  value: string;
}

export interface TaskDetailModel {
  typeLabel: "Projektaufgabe" | "Persönliche Aufgabe";
  /** Herkunft: Projekt- bzw. Listenname. */
  origin: string | null;
  ticket: string | null;
  description: { format: DescriptionFormat; value: string };
  /** Typspezifisches, eingeklappt unter den Kernfeldern. */
  more: DetailField[];
}

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

export function buildDetailModel(
  record: TaskRecord,
  resolve: {
    projectName?: (id: number) => string | undefined;
    listName?: (id: number) => string | undefined;
  } = {},
): TaskDetailModel {
  if (record.source === "project") {
    const t = record.raw as Task;
    const more: DetailField[] = [];
    const start = fmtDate(t.start_date);
    const end = fmtDate(t.end_date);
    if (start || end) more.push({ label: "Start / Ende", value: `${start ?? "—"} – ${end ?? "—"}` });
    if (t.estimated_hours) more.push({ label: "Schätzung", value: `${t.estimated_hours} h` });
    if (t.actual_hours) more.push({ label: "Gebucht", value: `${t.actual_hours} h` });
    if (t.dependency_count > 0) more.push({ label: "Abhängigkeiten", value: String(t.dependency_count) });
    if (t.subtask_count > 0) more.push({ label: "Sub-Aufgaben", value: String(t.subtask_count) });
    if (t.is_milestone) more.push({ label: "Meilenstein", value: "ja" });
    return {
      typeLabel: "Projektaufgabe",
      origin: record.projectId !== null ? resolve.projectName?.(record.projectId) ?? null : null,
      ticket: record.ticket,
      description: { format: "markdown", value: t.description ?? "" },
      more,
    };
  }
  const p = record.raw as PersonalTask & {
    recurrence?: unknown;
    reminder_at?: string | null;
  };
  const more: DetailField[] = [];
  const start = fmtDate(record.startDate);
  if (start) more.push({ label: "Start", value: start });
  if (p.recurrence) more.push({ label: "Wiederholung", value: "aktiv" });
  if (p.reminder_at) more.push({ label: "Erinnerung", value: new Date(p.reminder_at).toLocaleString("de-DE") });
  return {
    typeLabel: "Persönliche Aufgabe",
    origin: record.listId !== null ? resolve.listName?.(record.listId) ?? null : null,
    ticket: null,
    description: { format: "html", value: p.notes ?? "" },
    more,
  };
}

/**
 * Naechste/vorige Aufgabe fuer ↑/↓. Am Rand bleibt die Auswahl stehen (kein
 * Umlauf — sonst springt ↓ am Ende unbemerkt nach oben). Ist `uid` nicht
 * (mehr) in der Liste, gilt die erste Aufgabe.
 */
export function neighbourUid(uids: string[], uid: string | null, dir: 1 | -1): string | null {
  if (uids.length === 0) return null;
  const i = uid === null ? -1 : uids.indexOf(uid);
  if (i === -1) return uids[0]!;
  const j = Math.min(uids.length - 1, Math.max(0, i + dir));
  return uids[j]!;
}

/** Ein Schritt im gemeinsamen Konzept, egal aus welcher Quelle. */
export interface DetailStep {
  id: number;
  title: string;
  done: boolean;
  /** Referenz-Schritte (verlinkte Aufgabe) lassen sich nicht befoerdern. */
  canPromote: boolean;
}

export function stepsProgress(steps: DetailStep[]): { done: number; total: number } {
  return { done: steps.filter((s) => s.done).length, total: steps.length };
}

/**
 * Reihenfolge, in der die Liste Aufgaben ANZEIGT: Gruppen nacheinander, je
 * Gruppe in ihrer Sortierung; eingeklappte Gruppen fallen weg (ohne
 * Gruppierung gibt es nichts einzuklappen). Grundlage fuer ↑/↓ im
 * Detailbereich — die Rohreihenfolge der Datensaetze passt dazu nicht.
 */
export function displayOrder(
  buckets: { key: string; items: { uid: string }[] }[],
  collapsed: ReadonlySet<string> = new Set(),
  flat = false,
): string[] {
  const out: string[] = [];
  for (const b of buckets) {
    if (!flat && collapsed.has(b.key)) continue;
    for (const r of b.items) out.push(r.uid);
  }
  return out;
}
